<template>
  <div class="relative min-h-screen pt-32 pb-20 px-4">
    <div class="max-w-7xl mx-auto flex flex-col md:flex-row items-center gap-12">
      <div class="md:w-1/2 text-left z-10">
        <h1 class="text-6xl md:text-8xl font-black gradient-text leading-none mb-6">
          AUTHENTIQUE <br/> ARTIFICE
        </h1>
        <p class="text-xl text-gray-300 max-w-lg mb-8">
          Découvrez <strong>Authentique Artifice</strong>, votre boutique en ligne spécialisée. Accédez à la plus prestigieuse sélection d'<strong>artifices professionnels</strong>, pétards, jets de scène et fumigènes en France. 
        </p>
        <div class="flex gap-4">
          <router-link :to="{ name: 'category', params: { id: 'nuit' } }" class="btn-primary">
            Voir la Collection
          </router-link>
          <a href="#" class="px-8 py-3 rounded-full border border-white/20 hover:bg-white/5 transition-all text-white font-bold">
            Nos Prestations
          </a>
        </div>
      </div>
      
      <div class="md:w-1/2 relative">
        <img 
          src="/hero-bg.webp" 
          srcset="/hero-bg-mobile.webp 500w, /hero-bg.webp 600w"
          sizes="(max-width: 768px) 100vw, 592px"
          width="592"
          height="592"
          alt="Grand spectacle pyrotechnique - Authentique Artifice" 
          class="rounded-3xl shadow-2xl skew-y-3 hover:skew-y-0 transition-transform duration-700 shadow-fire-gold/20 w-full h-auto"
          fetchpriority="high"
          decoding="sync"
        />
        <div class="absolute -bottom-6 -left-6 bg-white/5 backdrop-blur-xl border border-white/10 p-6 rounded-2xl shadow-xl w-64 z-20">
          <p class="text-fire-gold font-black text-2xl">LIVRAISON 24H</p>
          <p class="text-sm text-gray-300">Expédition rapide et sécurisée pour vos événements.</p>
        </div>
      </div>
    </div>

    <!-- Trust Bar -->
    <div class="max-w-7xl mx-auto mt-20 py-10 border-y border-white/10 flex flex-wrap justify-between gap-8 px-8">
      <div v-for="trust in trustSignals" :key="trust.label" class="flex items-center gap-4 group">
        <div class="p-3 bg-white/5 rounded-2xl text-fire-gold group-hover:scale-110 transition-transform">
          <component :is="trust.icon" class="w-6 h-6" />
        </div>
        <div>
          <p class="text-white font-black text-sm uppercase tracking-tighter">{{ trust.label }}</p>
          <p class="text-gray-300 text-[10px] font-bold uppercase tracking-widest">{{ trust.sub }}</p>
        </div>
      </div>
    </div>

    <!-- Promo Banner (Packs) -->
    <div class="max-w-7xl mx-auto mt-20 px-4">
      <div class="relative overflow-hidden rounded-[48px] bg-gradient-to-r from-black to-fire-gold/20 border border-fire-gold/30 p-12 flex flex-col md:flex-row items-center gap-12 group">
        <div class="z-10 flex-1">
          <div class="inline-block px-4 py-1 bg-fire-gold text-black text-[10px] font-black uppercase tracking-widest rounded-full mb-6">
            HOT DEAL : -10% SUR LES PACKS
          </div>
          <h2 class="text-5xl font-black text-white uppercase tracking-tighter mb-4">PACK PRESTIGE #3</h2>
          <p class="text-gray-400 max-w-md mb-8">Le kit professionnel complet pour un mariage inoubliable : 12 stations de tir sans fil + 15 jets de scène (3m/60sec).</p>
          <div class="flex items-end gap-4 mb-8">
            <span class="text-4xl font-black text-white">347,72€</span>
            <span class="text-xl text-gray-400 line-through mb-1">386,35€</span>
          </div>
          <router-link :to="{ name: 'category', params: { id: 'packs' } }" class="btn-primary">
            Profiter de l'offre
          </router-link>
        </div>
        <div class="relative w-full md:w-1/2 flex justify-center">
          <img src="/img/promo/pack-prestige.webp" width="320" height="320" alt="Promotion Pack Prestige #3 - Feu d'artifice Authentique Artifice" class="w-80 h-80 object-cover rounded-3xl rotate-3 group-hover:rotate-0 transition-transform duration-700 shadow-2xl shadow-fire-gold/20" />
          <div class="absolute -top-4 -right-4 bg-fire-gold text-black w-24 h-24 rounded-full flex flex-col items-center justify-center font-black rotate-12">
            <span class="text-2xl">-10%</span>
            <span class="text-[8px] uppercase">Wedding</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Category Section -->
    <div class="max-w-7xl mx-auto mt-40">
      <h2 class="text-4xl font-black mb-12 text-center uppercase tracking-tighter">Nos Artifices par Catégorie</h2>
      <div class="grid grid-cols-2 md:grid-cols-4 gap-6">
        <router-link v-for="cat in categories" :key="cat.id" 
          :to="{ name: 'category', params: { id: cat.id } }"
          class="card-fire p-8 rounded-3xl flex flex-col items-center text-center gap-4 group"
        >
          <div class="p-4 bg-fire-gold/10 rounded-2xl text-fire-gold group-hover:bg-fire-gold group-hover:text-black transition-all">
            <component :is="getIcon(cat.icon)" class="w-10 h-10" />
          </div>
          <span class="text-xl font-bold">{{ cat.name }}</span>
        </router-link>
        
        <!-- Guide Card -->
        <router-link 
          to="/conseils-expert"
          class="card-fire p-8 rounded-3xl flex flex-col items-center text-center gap-4 group border-fire-gold/20"
        >
          <div class="p-4 bg-fire-gold/10 rounded-2xl text-fire-gold group-hover:bg-fire-gold group-hover:text-black transition-all">
            <ShieldCheckIcon class="w-10 h-10" />
          </div>
          <span class="text-xl font-bold uppercase tracking-tighter">Conseils & Sécurité</span>
        </router-link>
      </div>
    </div>

    <!-- Testimonials Section -->
    <div class="max-w-7xl mx-auto mt-40 pb-20 px-8">
      <div class="text-center mb-12">
        <div class="inline-flex items-center gap-2 px-4 py-2 bg-green-500/10 rounded-full border border-green-500/20 text-green-500 text-[10px] font-black uppercase tracking-widest mb-6">
          <CheckBadgeIcon class="w-4 h-4" /> Avis Vérifiés : 4.9/5
        </div>
        <h2 class="text-5xl font-black text-white uppercase tracking-tighter">CE QUE DISENT <br/> NOS ARTIFICIERS</h2>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
        <div v-for="review in reviews" :key="review.name" 
          class="p-8 rounded-[40px] bg-white/5 border border-white/10 relative group hover:border-fire-gold/30 transition-all duration-500"
        >
          <div class="flex gap-1 mb-4">
            <StarIcon v-for="i in 5" :key="i" class="w-5 h-5 text-fire-gold" />
          </div>
          <p class="text-gray-300 italic mb-6 leading-relaxed">"{{ review.text }}"</p>
          <div class="flex items-center gap-4">
            <div class="w-12 h-12 rounded-2xl bg-fire-gold/10 flex items-center justify-center border border-fire-gold/20 text-fire-gold font-black">
              {{ review.name[0] }}
            </div>
            <div>
              <p class="text-white font-black text-sm uppercase tracking-tighter">{{ review.name }}</p>
              <p class="text-gray-400 text-[10px] uppercase font-bold tracking-widest">{{ review.date }}</p>
            </div>
          </div>
          <div class="absolute top-6 right-8 text-white/5">
            <ChatBubbleBottomCenterTextIcon class="w-16 h-16" />
          </div>
        </div>
      </div>
    </div>

    <!-- Latest Blog Articles -->
    <div class="max-w-7xl mx-auto mt-40 pb-40 px-8">
      <div class="flex justify-between items-end mb-12">
        <div>
          <h2 class="text-5xl font-black text-white uppercase tracking-tighter mb-4">DERNIERS <br/> CONSEILS</h2>
          <p class="text-gray-400 font-bold max-w-md">L'expertise AuthentiqueArtifice pour préparer vos spectacles comme un professionnel.</p>
        </div>
        <router-link :to="{ name: 'guide' }" class="text-fire-gold font-black uppercase text-xs tracking-widest hover:underline flex items-center gap-2 mb-4">
          VOIR TOUS LES GUIDES <ArrowRightIcon class="w-4 h-4" />
        </router-link>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
        <router-link 
          v-for="article in latestArticles" 
          :key="article.id"
          :to="'/blog/' + article.slug"
          class="group bg-white/5 border border-white/10 rounded-[32px] overflow-hidden hover:border-fire-gold/30 transition-all duration-500"
        >
          <div class="aspect-video relative overflow-hidden">
            <img 
              :src="article.image_cover" 
              :srcset="`${article.image_cover.replace('.webp', '-mobile.webp')} 330w, ${article.image_cover} 800w`"
              sizes="(max-width: 768px) 100vw, 382px"
              width="382"
              height="215"
              :alt="article.title" 
              class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 opacity-60 group-hover:opacity-100" 
              loading="lazy" 
              decoding="async" 
            />
            <div class="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0A0A0A] to-transparent p-6 pt-12">
               <span class="text-fire-gold text-[10px] font-black uppercase tracking-widest bg-fire-gold/10 px-3 py-1 rounded-full border border-fire-gold/20">
                 {{ article.category_name || 'Expert' }}
               </span>
            </div>
          </div>
          <div class="p-8">
            <h2 class="text-2xl font-black text-white uppercase leading-none mb-4 group-hover:text-fire-gold transition-colors">{{ article.title }}</h2>
            <p class="text-gray-400 text-sm font-bold line-clamp-2">{{ article.excerpt }}</p>
          </div>
        </router-link>
      </div>
    </div>
    
    <!-- SEO Content Section -->
    <div class="max-w-7xl mx-auto mb-40 px-8 py-20 border-t border-white/5">
      <div class="grid md:grid-cols-2 gap-12 items-center">
        <div>
          <h2 class="text-3xl font-black text-white uppercase tracking-tighter mb-6">Expertise & Sécurité Pyrotechnique</h2>
          <p class="text-gray-400 leading-relaxed mb-6">
            En tant qu'<strong>artificier professionnel</strong>, Authentique Artifice s'engage à vous fournir des produits <strong>homologués CE</strong> répondant aux normes de sécurité les plus strictes. Que vous organisiez un <strong>feu d'artifice de mariage</strong>, un anniversaire ou une fête nationale, notre sélection d'artifices automatiques et de <strong>jets de scène pour événements</strong> vous garantit un spectacle grandiose.
          </p>
          <p class="text-gray-400 leading-relaxed mb-6">
            Nous assurons une <strong>livraison rapide sécurisée</strong> partout en France, notamment à <strong>Paris, Marseille, Lyon, Toulouse, Nice, Nantes, Montpellier, Strasbourg, Bordeaux et Lille</strong>.
          </p>
          <p class="text-gray-400 leading-relaxed">
            Spécialiste de la <strong>vente de feux d'artifice aux particuliers</strong>, nous vous accompagnons avec des conseils d'experts pour réussir votre spectacle pyrotechnique en toute sécurité.
          </p>
        </div>
        <div class="bg-white/5 p-8 rounded-[40px] border border-white/10">
          <ul class="space-y-4">
            <li class="flex items-center gap-4 text-white font-bold">
              <div class="w-1.5 h-1.5 bg-fire-gold rounded-full"></div>
              Boutique en ligne certifiée
            </li>
            <li class="flex items-center gap-4 text-white font-bold">
              <div class="w-1.5 h-1.5 bg-fire-gold rounded-full"></div>
              Artifices homologués F2 / F3
            </li>
            <li class="flex items-center gap-4 text-white font-bold">
              <div class="w-1.5 h-1.5 bg-fire-gold rounded-full"></div>
              Vente aux particuliers et professionnels
            </li>
            <li class="flex items-center gap-4 text-white font-bold">
              <div class="w-1.5 h-1.5 bg-fire-gold rounded-full"></div>
              Conseils techniques d'artificiers
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import api from '../services/api';
import { 
  SparklesIcon, 
  CloudIcon, 
  HeartIcon, 
  UsersIcon, 
  BoltIcon,
  SunIcon,
  FireIcon as BombIcon,
  StarIcon,
  ShieldCheckIcon,
  TruckIcon,
  FaceSmileIcon,
  CheckBadgeIcon,
  ChatBubbleBottomCenterTextIcon,
  GiftIcon,
  ArrowRightIcon
} from '@heroicons/vue/24/solid';

import { useCategoryStore } from '../stores/categories';
import { computed } from 'vue';

const categoryStore = useCategoryStore();
const categories = computed(() => categoryStore.categories);
const latestArticles = ref([]);

const trustSignals = [
  { label: 'Livraison 24/48h', sub: 'Expédition rapide', icon: TruckIcon },
  { label: 'Paiement Sécurisé', sub: 'SSL & 3D Secure', icon: ShieldCheckIcon },
  { label: 'Support 24/7', sub: 'Expert à votre écoute', icon: FaceSmileIcon },
  { label: 'Produits Homologués', sub: 'Normes CE & Sécurité', icon: SparklesIcon }
];

const reviews = [
  { name: 'Mickael L.', text: 'Une sélection incroyable pour mon mariage. Le pack VIP a bluffé tous mes invités ! Livraison ultra rapide en 24h.', date: 'Il y a 2 jours' },
  { name: 'Sophie D.', text: 'Des fumigènes de super qualité pour mon shooting photo. Les couleurs sont denses et éclatantes. Je recommande.', date: 'Il y a 1 semaine' },
  { name: 'Julien R.', text: 'Service client au top. Ils m\'ont conseillé sur les distances de sécurité pour ma fête d\'anniversaire. Très pro.', date: 'Il y a 3 jours' }
];

const getIcon = (name) => {
  const icons = {
    SparklesIcon,
    BombIcon,
    CloudIcon,
    HeartIcon,
    UsersIcon,
    BoltIcon,
    SunIcon,
    GiftIcon
  };
  return icons[name] || SparklesIcon;
};

onMounted(async () => {
  categoryStore.fetchCategories();
  
  const blogRes = await api.get('/admin/manage_blog.php', { params: { action: 'list', limit: 3 } });
  latestArticles.value = blogRes.data;

  // Canonical
  let canonical = document.querySelector('link[rel="canonical"]');
  if (canonical) canonical.setAttribute('href', window.location.origin + '/');
});
</script>
